// src/components/OrgAnalyticsEntitlements.jsx
//
// Read-only org-admin view of analytics entitlements.
// Mirrors the platform-admin OrgAnalyticsPanel but without grant/edit.
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { productDisplayName } from "../constants/analyticsProducts";

export default function OrgAnalyticsEntitlements() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchWithAuth("/api/v1/org/analytics-grants");
        if (!res.ok) throw new Error("Failed to load analytics entitlements");
        setData(await res.json());
      } catch (e) {
        console.error(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center pt-6">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  if (error) return <div className="text-xs text-red-400">{error}</div>;
  if (!data) return null;

  const { product_summary = [], grants = [] } = data;

  if (product_summary.length === 0 && grants.length === 0) {
    return (
      <div className="text-gray-400 italic mt-4">
        No analytics products entitled to this organization.
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      {/* PRODUCT POOL SUMMARY */}
      <div className="rounded border border-bg-border bg-bg p-3 space-y-3">
        <div className="text-xs text-gray-300 font-semibold">Product Pools</div>

        {product_summary.map(s => {
          const consumed = Number(s.sq_mi_consumed) || 0;
          const total = Number(s.sq_mi_purchased_total) || 0;
          const isFlat = s.unit === "flat";
          const label = productDisplayName(s.product_code, s.display_name);

          if (isFlat) {
            return (
              <div key={s.product_code} className="flex justify-between items-center text-xs">
                <span className="text-gray-400">
                  {label}
                  {s.is_bundle && (
                    <span className="ml-2 text-accent-info text-[10px] uppercase">bundle</span>
                  )}
                </span>
                <span className="text-accent-info text-[10px] uppercase">
                  active · flat-rate
                </span>
              </div>
            );
          }

          const pct = total > 0 ? (consumed / total) * 100 : 0;
          const available = Math.max(0, total - consumed);

          return (
            <div key={s.product_code}>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">
                  {label}
                  {s.is_bundle && (
                    <span className="ml-2 text-accent-info text-[10px] uppercase">bundle</span>
                  )}
                </span>
                <span className="text-gray-100">
                  {consumed.toFixed(1)} / {total.toFixed(1)} sq mi{" "}
                  <span className="text-fg-secondary font-normal">
                    ({available.toFixed(1)} available)
                  </span>
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded bg-bg-soft">
                <div
                  className={`h-1.5 rounded transition-[width] duration-200 ${
                    pct >= 100 ? "bg-red-500"
                      : pct >= 80 ? "bg-yellow-500"
                        : "bg-status-ok"
                  }`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* GRANTS TABLE */}
      <div>
        <div className="text-xs text-gray-300 font-semibold mb-2">
          Analytics Grants
        </div>

        <table className="w-full text-xs">
          <thead className="text-gray-400">
            <tr>
              <th className="text-left py-1">Product</th>
              <th className="text-left py-1">AOI</th>
              <th className="text-left py-1">Allocated</th>
              <th className="text-left py-1">Consumed</th>
              <th className="text-left py-1">Term</th>
              <th className="text-left py-1">Contract</th>
              <th className="text-left py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {grants.map(g => {
              const termStart = new Date(g.term_start).toLocaleDateString();
              const termEnd = g.term_end
                ? new Date(g.term_end).toLocaleDateString()
                : "—";

              const statusColor = {
                active: "border-green-500/40 text-green-400",
                suspended: "border-yellow-500/40 text-yellow-400",
                revoked: "border-red-500/40 text-red-400",
                expired: "border-gray-500/40 text-gray-400",
              }[g.status] || "border-gray-500/40 text-gray-400";

              return (
                <tr key={g.id} className="border-t border-bg-border">
                  <td className="py-1.5">
                    {productDisplayName(g.product_code, g.product_display_name)}
                    {g.is_bundle && (
                      <span className="ml-1 text-accent-info text-[10px] uppercase">bundle</span>
                    )}
                  </td>
                  <td className="py-1.5 text-gray-400">{g.aoi_name || "—"}</td>
                  <td className="py-1.5">
                    {g.sq_mi_purchased != null
                      ? `${Number(g.sq_mi_purchased).toFixed(1)} sq mi`
                      : "unmetered"}
                  </td>
                  <td className="py-1.5 text-gray-300">
                    {Number(g.sq_mi_consumed).toFixed(1)}
                  </td>
                  <td className="py-1.5 text-gray-400">
                    {termStart} – {termEnd}
                  </td>
                  <td className="py-1.5 text-gray-400">{g.contract_ref || "—"}</td>
                  <td className="py-1.5">
                    <span className={`px-2 py-0.5 rounded border ${statusColor}`}>
                      {g.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}